const load = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = url;
  });

type Meta = { orient: number; w: number; h: number };

const jpegMeta = async (blob: Blob): Promise<Meta> => {
  const buf = await blob.slice(0, 256 * 1024).arrayBuffer();
  const v = new DataView(buf);
  const meta: Meta = { orient: 1, w: 0, h: 0 };
  if (v.byteLength < 4 || v.getUint16(0) !== 0xffd8) return meta;
  let off = 2;
  while (off + 4 < v.byteLength) {
    if (v.getUint8(off) !== 0xff) break;
    const marker = v.getUint16(off);
    off += 2;
    if (marker === 0xffd8) continue;
    if (marker === 0xffd9 || marker === 0xffda) break;
    if (off + 2 > v.byteLength) break;
    const size = v.getUint16(off);
    if (size < 2 || off + size > v.byteLength) break;
    if (marker === 0xffe1) meta.orient = parseExif(v, off + 2) ?? meta.orient;
    const sof =
      marker >= 0xffc0 && marker <= 0xffcf && marker !== 0xffc4 && marker !== 0xffc8 && marker !== 0xffcc;
    if (sof && off + 7 < v.byteLength) {
      meta.h = v.getUint16(off + 3);
      meta.w = v.getUint16(off + 5);
    }
    off += size;
  }
  return meta;
};

const parseExif = (v: DataView, start: number): number | null => {
  if (start + 14 > v.byteLength) return null;
  const ascii = String.fromCharCode(
    v.getUint8(start),
    v.getUint8(start + 1),
    v.getUint8(start + 2),
    v.getUint8(start + 3),
  );
  if (ascii !== "Exif") return null;
  const tiff = start + 6;
  const le = v.getUint16(tiff) === 0x4949;
  const u16 = (o: number) => v.getUint16(o, le);
  const u32 = (o: number) => v.getUint32(o, le);
  let dir = tiff + u32(tiff + 4);
  if (dir + 2 > v.byteLength) return null;
  const n = u16(dir);
  dir += 2;
  for (let i = 0; i < n && dir + 12 <= v.byteLength; i++, dir += 12) {
    if (u16(dir) === 0x0112) return u16(dir + 8) || 1;
  }
  return null;
};

type Src = { width: number; height: number; draw: CanvasImageSource };

const pixels = async (blob: Blob, url: string): Promise<Src> => {
  try {
    const bmp = await createImageBitmap(blob, { imageOrientation: "none" });
    return { width: bmp.width, height: bmp.height, draw: bmp };
  } catch {
    const img = await load(url);
    return { width: img.naturalWidth, height: img.naturalHeight, draw: img };
  }
};

const paint = (src: Src, o: number) => {
  const w = src.width;
  const h = src.height;
  const canvas = document.createElement("canvas");
  const swapped = o >= 5 && o <= 8;
  canvas.width = swapped ? h : w;
  canvas.height = swapped ? w : h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("no canvas");
  switch (o) {
    case 2: ctx.transform(-1, 0, 0, 1, w, 0); break;
    case 3: ctx.transform(-1, 0, 0, -1, w, h); break;
    case 4: ctx.transform(1, 0, 0, -1, 0, h); break;
    case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
    case 6: ctx.transform(0, 1, -1, 0, h, 0); break;
    case 7: ctx.transform(0, -1, -1, 0, h, w); break;
    case 8: ctx.transform(0, -1, 1, 0, 0, w); break;
  }
  ctx.drawImage(src.draw, 0, 0);
  return canvas;
};

const alreadyOriented = (src: Src, meta: Meta) => {
  if (!meta.w || !meta.h) return false;
  if (meta.orient >= 5 && meta.orient <= 8) {
    return src.width === meta.h && src.height === meta.w;
  }
  return false;
};

export const toJpeg = async (blob: Blob): Promise<Blob> => {
  const meta = await jpegMeta(blob);
  const url = URL.createObjectURL(blob);
  try {
    const src = await pixels(blob, url);
    const sheet = alreadyOriented(src, meta) ? paint(src, 1) : paint(src, meta.orient);
    const scale = Math.min(1, 1600 / Math.max(sheet.width, sheet.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(sheet.width * scale));
    canvas.height = Math.max(1, Math.round(sheet.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("no canvas");
    ctx.drawImage(sheet, 0, 0, canvas.width, canvas.height);
    if (src.draw instanceof ImageBitmap) src.draw.close();
    return await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("encode failed"))), "image/jpeg", 0.8);
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};
